// src/components/LogoutButton.js
import React, { useState } from "react";
import ReactDOM from "react-dom"; // 👈 Import ReactDOM
import { auth, rtdb } from "../firebase";
import { signOut } from "firebase/auth";
import { ref, set, serverTimestamp } from "firebase/database";
import { useTheme } from "../context/ThemeContext";

export default function LogoutButton() {
  const [showModal, setShowModal] = useState(false);
  const { isDarkMode } = useTheme();

  const handleLogout = async () => {
    try {
      // 🟢 Set status to OFFLINE *before* removing auth token
      // This prevents "Permission Denied" errors during the cleanup race condition
      if (auth.currentUser) {
        const userStatusRef = ref(rtdb, `/status/${auth.currentUser.uid}`);
        await set(userStatusRef, {
          state: "offline",
          last_changed: serverTimestamp(),
        });
      }

      await signOut(auth);
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  // 🟢 Modal Content using Portal
  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fadeIn">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={() => setShowModal(false)}
      ></div>

      {/* Modal Card */}
      <div className={`relative w-full max-w-xs max-h-[90vh] overflow-y-auto rounded-2xl p-6 shadow-2xl scale-100 transform transition-all border ${isDarkMode ? 'bg-gray-900 border-white/10 text-white' : 'bg-white border-white/20 text-gray-900'}`}>
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 p-3 rounded-full bg-red-100 text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          </div>
          <h3 className="text-xl font-bold mb-2">Log Out</h3>
          <p className={`mb-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Are you sure you want to sign out of your account?
          </p>

          <div className="flex gap-3 w-full">
            <button
              onClick={() => setShowModal(false)}
              className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${isDarkMode ? 'bg-white/10 hover:bg-white/20 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 px-4 py-2.5 rounded-xl font-medium bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30 transition-all"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`transition-colors p-2 rounded-full ${showModal ? 'text-red-500 bg-red-50' : 'text-gray-500 hover:text-red-500 hover:bg-red-50/50'}`}
        title="Logout"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      </button>

      {/* Render Portal */}
      {showModal && ReactDOM.createPortal(modalContent, document.body)}
    </>
  );
}
